/**
 * Add Supplier Group wizard (Suppliers page → Add menu).
 *
 * Creates the CompanyGroup that supplier companies are grouped under — the
 * grouping behind the Suppliers page's "Supplier Groups" tab.
 *
 * CR-6654: step 2 requires the group to get at least one supplier, either by
 * assigning existing ones or by creating a new one under it. That is not
 * red tape: a CompanyGroup has no type of its own, so a "supplier group" is
 * only one *because* supplier companies point at it. A group with no members
 * is untyped and shows nowhere it matters, so refusing to create one prevents
 * the broken state instead of detecting it afterwards.
 *
 * Its own component because Suppliers.tsx is already over the 500-line limit.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI, suppliersAPI, apiErrorMessage } from '../../lib/api';

interface AddSupplierGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Refresh the list so the new group is visible. */
  onCreated: () => void;
  /** Suppliers that can be put in the group — the page's current directory. */
  supplierOptions: { id: number; name: string }[];
  /**
   * The customer a newly created supplier is linked to, resolved by the page
   * from the active view. A supplier linked to no customer is invisible, so
   * the "create new" branch is disabled without one.
   */
  customerTarget: { ids: number[]; label: string } | null;
}

const INITIAL_FORM = {
  name: '',
  description: '',
  headquarters_country: '',
  website: '',
};

type MemberMode = 'existing' | 'new';

export default function AddSupplierGroupModal({
  open, onOpenChange, onCreated, supplierOptions, customerTarget,
}: AddSupplierGroupModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<MemberMode>('existing');
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [newSupplierName, setNewSupplierName] = useState('');
  // CR-7774: a supplier created here needs a city like every other route.
  const [newSupplierCity, setNewSupplierCity] = useState('');

  const canCreateNew = !!customerTarget?.ids.length;

  const reset = () => {
    setStep(1);
    setForm(INITIAL_FORM);
    setMode('existing');
    setMemberIds([]);
    setMemberSearch('');
    setNewSupplierName('');
    setNewSupplierCity('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleMember = (id: number) => {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const visibleOptions = memberSearch.trim()
    ? supplierOptions.filter((s) =>
        s.name.toLowerCase().includes(memberSearch.trim().toLowerCase()))
    : supplierOptions;

  // Step 2 is satisfied by either branch — never by neither.
  // CR-7774: the new-supplier branch now also needs a city, so Create Group
  // stays disabled until one is given rather than failing on the API's guard.
  const hasMembers = mode === 'existing'
    ? memberIds.length > 0
    : newSupplierName.trim().length > 0 && newSupplierCity.trim().length > 0 && canCreateNew;

  const handleSubmit = async () => {
    if (!form.name.trim() || !hasMembers) return;
    setSaving(true);
    try {
      let supplierIds = memberIds;

      // Create the supplier first: if the group create then fails, the supplier
      // is still a valid approved supplier the user asked for — whereas a group
      // created first and left empty would be invisible and unreachable.
      if (mode === 'new') {
        const created = await suppliersAPI.createFromWizard({
          company_name: newSupplierName.trim(),
          // CR-7774: mandatory on every supplier-creation route.
          address_city: newSupplierCity.trim(),
          customer_id: customerTarget!.ids[0],
          customer_ids: customerTarget!.ids,
          approved: true,
        });
        supplierIds = [created.data.supplier_id];
      }

      await coreAPI.createCompanyGroup({
        name: form.name.trim(),
        description: form.description.trim(),
        headquarters_country: form.headquarters_country.trim(),
        website: form.website.trim(),
        // CR-6682: state the side explicitly. Without it the group is stored
        // untyped and Organisation Setup lists it under Customers as well.
        company_type: 'supplier',
        supplier_ids: supplierIds,
      });
      toast.success(
        `Supplier group "${form.name.trim()}" created with ${supplierIds.length} supplier${supplierIds.length === 1 ? '' : 's'}`
      );
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to create supplier group'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Supplier Group</DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Step 1 of 2 — name the group.'
              : 'Step 2 of 2 — every group needs at least one supplier, or it will not appear anywhere.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Group Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Acme Holdings"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Headquarters Country</Label>
                <Input
                  value={form.headquarters_country}
                  onChange={(e) => setForm({ ...form, headquarters_country: e.target.value })}
                  placeholder="e.g. United Kingdom"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === 'existing' ? 'default' : 'outline'}
                onClick={() => setMode('existing')}
              >
                Assign existing
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'new' ? 'default' : 'outline'}
                onClick={() => setMode('new')}
              >
                Create new supplier
              </Button>
            </div>

            {mode === 'existing' ? (
              <div className="space-y-1.5">
                <Label>
                  Suppliers in this group *
                  {memberIds.length > 0 && (
                    <span className="ml-2 font-normal text-muted-foreground">
                      {memberIds.length} selected
                    </span>
                  )}
                </Label>
                {supplierOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No suppliers in the current view — create one instead.
                  </p>
                ) : (
                  <>
                    {supplierOptions.length > 8 && (
                      <Input
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Search suppliers…"
                      />
                    )}
                    <div className="max-h-56 overflow-y-auto rounded-md border divide-y">
                      {visibleOptions.map((s) => (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted"
                        >
                          <input
                            type="checkbox"
                            checked={memberIds.includes(s.id)}
                            onChange={() => toggleMember(s.id)}
                          />
                          {s.name}
                        </label>
                      ))}
                      {!visibleOptions.length && (
                        <p className="px-3 py-2 text-sm text-muted-foreground">
                          No suppliers match your search.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="new-supplier-name">New supplier name <span className="text-red-500">*</span></Label>
                <Input
                  id="new-supplier-name"
                  value={newSupplierName}
                  onChange={(e) => setNewSupplierName(e.target.value)}
                  placeholder="e.g. Acme Castings Ltd"
                />
                {/* CR-7774: City / Town is mandatory on every route that creates
                    a supplier. This branch collected a name only, so a supplier
                    born here had no city — the same gap the CR closes on the
                    wizard and the Add Approved Supplier modal. */}
                <Label htmlFor="new-supplier-city" className="pt-2 block">
                  City / Town <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="new-supplier-city"
                  value={newSupplierCity}
                  onChange={(e) => setNewSupplierCity(e.target.value)}
                  placeholder="City or town"
                  required
                />
                {canCreateNew ? (
                  <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground">Added as an approved supplier for</span>
                    <span className="font-medium">{customerTarget?.label}</span>
                  </div>
                ) : (
                  <p className="text-sm text-amber-700">
                    Select a site or company in the view switcher first — a new supplier
                    needs a customer to belong to.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={!form.name.trim()}>Next</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>Back</Button>
              <Button onClick={handleSubmit} disabled={saving || !hasMembers}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Group
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
